<template>
    <div>
        <breadcrumbs />
        <div class="mb-4 d-flex flex-wrap">
            <v-btn variant="flat" :to="{name: 'campaign-details', params: {id: 0}}">
                {{ $t('create') }}
            </v-btn>
        </div>
        <campaigns
            :campaigns="campaigns"
            :is-campaigns-loading="isCampaignsLoading"
            :campaigns-total-count="campaignsTotalCount"
            @get-campaigns="getCampaigns"
        >
            <template v-slot="{getCampaigns}">
                <campaigns-filters
                    v-model="campaignsFilters"
                    :has-active-filters="hasActiveFilters"
                    @submit="getCampaigns"
                    @reset="resetCampaignsFilters(getCampaigns)"
                />
            </template>
        </campaigns>
    </div>
</template>

<script lang="ts">
    import {Component, Vue} from 'vue-facing-decorator'
    import {clean, clone, formatDateToISO, isEqual} from '@/utils/helpers'
    import Breadcrumbs from '~src/components/Breadcrumbs.vue'
    import Campaigns from '@/components/account/campaigns/Campaigns.vue'
    import CampaignsFilters from '@/components/account/campaigns/CampaignsFilters.vue'
    import type {CampaignType} from '~/utils/types/account/campaigns/types'

    const initCampaignsFilters = () => ({
        assignedToId: null,
        budgetFrom: null,
        budgetTo: null,
        budgetCurrency: null,
        campaignType: null,
        dateFrom: null,
        dateTo: null,
        dateType: 'CREATION',
        name: null,
        status: true,
    })

    @Component({
        components: {
            Breadcrumbs,
            Campaigns,
            CampaignsFilters,
        },
    })
    export class CampaignsPage extends Vue {
        isCampaignsLoading: boolean = true
        campaigns: CampaignType[] = []
        campaignsTotalCount: number = 0
        campaignsFilters = initCampaignsFilters()
        dateTypes = {
            CREATION: {
                from: 'createdDateFrom',
                to: 'createdDateTo',
            },
            MODIFIED: {
                from: 'modifiedDateFrom',
                to: 'modifiedDateTo',
            },
            EXECUTION: {
                from: 'campaignDateFrom',
                to: 'campaignDateTo',
            },
        }

        async getCampaigns({itemsPerPage, page}): Promise<void> {
            try {
                this.isCampaignsLoading = true
                const filters = clone(this.campaignsFilters)

                if (filters.dateFrom) {
                    const activeKeyDateFrom = this.dateTypes[filters.dateType]?.from || 'createdDateFrom'
                    filters[activeKeyDateFrom] = formatDateToISO(filters.dateFrom)
                    delete filters.dateFrom
                }
                if (filters.dateTo) {
                    const activeKeyDateTo = this.dateTypes[filters.dateType]?.to || 'createdDateTo'
                    filters[activeKeyDateTo] = formatDateToISO(filters.dateTo)
                    delete filters.dateTo
                }

                delete filters.dateType
                clean(filters)

                const {campaigns, count} = await this.$api.campaigns.get({
                    ...filters,
                    limit: itemsPerPage,
                    offset: itemsPerPage * (page - 1),
                })

                this.campaigns = campaigns
                this.campaignsTotalCount = count
            } catch (e) {
                this.campaigns = []
                this.campaignsTotalCount = 0
            } finally {
                this.isCampaignsLoading = false
            }
        }

        resetCampaignsFilters(getCampaigns): void {
            this.campaignsFilters = initCampaignsFilters()
            getCampaigns()
        }

        get hasActiveFilters(): boolean {
            return !isEqual(initCampaignsFilters(), this.campaignsFilters)
        }
    }

    export default CampaignsPage
</script>
